/**
 * HeartbeatBar
 *
 * A subtle SVG pulse bar that sits beneath the "Love Language FM" wordmark.
 * It draws 5 thin bars arranged like a micro-EQ / heartbeat trace.
 *
 * Design rules:
 *   - Uses brand red only (--absa-red)
 *   - Bars breathe at the given BPM — never spike, never flash
 *   - Idle: bars nearly invisible, barely breathing
 *   - Active: a calm, human rhythm — slower than an equaliser
 *   - Heartbeat mode: subtle colour brightens slightly
 *   - Honours prefers-reduced-motion (static, near-invisible)
 */

import { useRef, useEffect, useCallback } from "react";

interface HeartbeatBarProps {
  bpm?: number;           // beats per minute — controls animation speed
  isHeartbeat?: boolean;  // true = mic is synced to real heartbeat
  isListening?: boolean;  // mic is active
  width?: number;         // total bar group width in px
  height?: number;        // max bar height in px
  className?: string;
}

// Bar shape definitions: relative widths and heights (0–1)
const BARS = [
  { h: 0.30, phase: 0.00 },
  { h: 0.65, phase: 0.20 },
  { h: 1.00, phase: 0.10 },
  { h: 0.55, phase: 0.35 },
  { h: 0.25, phase: 0.50 },
];

export default function HeartbeatBar({
  bpm = 70,
  isHeartbeat = false,
  isListening = false,
  width = 28,
  height = 8,
  className = "",
}: HeartbeatBarProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number | null>(null);
  const t0        = useRef<number>(0);

  const reduced = typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const draw = useCallback((ts: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx2d = canvas.getContext("2d");
    if (!ctx2d) return;

    if (!t0.current) t0.current = ts;
    const elapsed = (ts - t0.current) / 1000; // seconds

    const beatPeriod = 60 / bpm;    // seconds per beat

    const dpr = window.devicePixelRatio || 1;
    const W = canvas.width  / dpr;
    const H = canvas.height / dpr;

    ctx2d.clearRect(0, 0, canvas.width, canvas.height);

    const barW = W / (BARS.length * 2 - 1);
    const gap  = barW;

    BARS.forEach((bar, i) => {
      // Breathing envelope: sine wave synced to BPM
      const phase = bar.phase * beatPeriod;
      // A single heartbeat "lub": rises quickly, falls slowly
      const t = ((elapsed + phase) % beatPeriod) / beatPeriod; // 0–1
      // Shape: quick attack (0→0.15), sustain, slow decay
      let env: number;
      if (t < 0.15) {
        env = t / 0.15;                // rise
      } else {
        env = 1 - ((t - 0.15) / 0.85); // decay
      }
      env = Math.max(0, env);

      // Idle: barely visible; active: full range
      const minH = H * bar.h * 0.12;
      const maxH = H * bar.h;
      const barH = minH + (maxH - minH) * env;

      const x = i * (barW + gap);
      const y = (H - barH) / 2;

      // Colour: brand red, opacity modulated
      const baseOpacity = isListening ? 0.45 : 0.18;
      const peakOpacity = isHeartbeat ? 0.85 : 0.65;
      const opacity = baseOpacity + (peakOpacity - baseOpacity) * env;

      ctx2d.fillStyle = `hsla(354, 68%, 40%, ${opacity})`;
      ctx2d.beginPath();
      ctx2d.roundRect(x, y, barW, barH, barW / 2);
      ctx2d.fill();
    });

    rafRef.current = requestAnimationFrame(draw);
  }, [bpm, isHeartbeat, isListening]);

  useEffect(() => {
    if (reduced) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width  = width  * dpr;
    canvas.height = height * dpr;

    const ctx2d = canvas.getContext("2d");
    if (ctx2d) {
      ctx2d.scale(dpr, dpr);
    }

    t0.current = 0;
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [draw, width, height, reduced]);

  if (reduced) {
    // Completely static — barely visible
    return (
      <span
        className={`inline-flex items-center gap-[2px] ${className}`}
        style={{ width, height }}
        aria-hidden
      >
        {BARS.map((b, i) => (
          <span
            key={i}
            style={{
              display: "block",
              width: width / (BARS.length * 2 - 1),
              height: height * b.h * 0.15,
              background: "hsl(354 68% 40% / 0.15)",
              borderRadius: 999,
            }}
          />
        ))}
      </span>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ width, height }}
      className={`block ${className}`}
      aria-hidden
    />
  );
}
